import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Group } from '../../../models/group.model';
import { SchoolMemberType } from '../../../models/member.school.model';
import { MembershipStatus } from '../../../models/membership.status';
import { UserModel } from '../../../models/user.model';
import { SchoolMemberService } from '../../../services/member.school.service';
import { SchoolRolesService } from '../../../services/school-roles.service';
import * as gradeActions from '../../../state/actions/grade.school.actions';
import * as groupActions from '../../../state/actions/group.actions';
import * as lessonActions from '../../../state/actions/lesson.school.actions';
import * as sessionActions from '../../../state/actions/session.school.actions';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnFabComponent } from '../../components/buttons/btn-fab/btn-fab.component';
import { BtnIconComponent } from '../../components/buttons/btn-icon/btn-icon.component';
import { BtnRouteComponent } from '../../components/buttons/btn-route/btn-route.component';
import { AcademicStatusComponent } from './charts/academic-status/academic-status.component';
import { StudentsByGradeComponent } from './charts/students-by-grade/students-by-grade.component';
import type { AnalyticsSection } from '../analytics/school-analytics.component';
// Only used inside @defer blocks, so it loads as its own chunk
import { SchoolAnalyticsComponent } from '../analytics/school-analytics.component';
import { AnalyticsSkeletonComponent } from '../analytics/analytics-skeleton/analytics-skeleton.component';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    RouterLink,
    AsyncPipe,
    BtnBackComponent,
    BtnIconComponent,
    BtnFabComponent,
    BtnRouteComponent,
    StudentsByGradeComponent,
    AcademicStatusComponent,
    BtnIconComponent,
    SchoolAnalyticsComponent,
    AnalyticsSkeletonComponent,
    NgTemplateOutlet,
    NgClass,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent implements OnInit, OnDestroy {
  dropdownPopoverShow = false;

  memberService: SchoolMemberService = inject(SchoolMemberService);
  private logger = inject(NGXLogger);

  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  store: Store<AppState> = inject(Store<AppState>);
  roles: SchoolRolesService = inject(SchoolRolesService);

  groupId: string | null = null;

  group$: Observable<Group | null | undefined> = new Observable();

  user$: Observable<UserModel | null> = new Observable();

  loadingStats$: Observable<boolean> = new Observable();

  memberStatsLoading$: Observable<boolean> = new Observable();

  membershipStatus = MembershipStatus;
  activeTab: AnalyticsSection = 'overview';
  memberType = SchoolMemberType;

  myRoles$: Observable<any> = new Observable();

  exit$: Subject<void> = new Subject();

  constructor() {
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.exit$))
      .subscribe((params) => {
        this.groupId = params.get('id');
      });

    this.user$ = this.store.select((state) => state.user.user);

    this.loadingStats$ = this.store
      .select((state) => state.groups)
      .pipe(takeUntil(this.exit$), select(groupSelector.statsLoading));

    this.group$ = this.store.select(groupSelector.getSelectedGroup);

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  ngOnInit() {}

  @HostListener('document:click')
  onDocumentClick() {
    this.dropdownPopoverShow = false;
  }



  refreshLessons() {
    this.store.dispatch(
      lessonActions.loadLessonStats({ groupId: this.groupId! }),
    );
  }

  refreshStudents() {
    this.store.dispatch(
      gradeActions.loadGradeStats({ groupId: this.groupId! }),
    );
  }

  refreshStats() {
    this.store.dispatch(
      groupActions.reloadGroupStats({ groupId: this.groupId! }),
    );
  }

  refreshSessions() {
    this.store.dispatch(
      sessionActions.loadSessionStats({ groupId: this.groupId! }),
    );
  }

  setActiveTab(tab: AnalyticsSection) {
    this.logger.debug(`DashboardComponent :: setActiveTab: ${tab}`);
    this.activeTab = tab;
  }

  toggleDropdown(event: any) {
    event.preventDefault();
    if (this.dropdownPopoverShow) {
      this.dropdownPopoverShow = false;
    } else {
      this.dropdownPopoverShow = true;
    }
  }
}
